"use client"

import { useEffect, useState } from "react"
import { createClient } from "@/lib/supabase/client"
import type { UserResponse } from "@supabase/supabase-js"
import { useUserRole } from "@/providers/user-role-provider"
import { PublicNavGlass } from "@/components/public-nav-glass"

/**
 * The glass nav, for logged-out visitors only.
 *
 * PublicNavGlass does not hide itself when someone is signed in — it swaps the
 * Join pill for Dashboard and keeps rendering. That is right for the marketing
 * pages it was built for, and wrong for a content page, where a signed-in
 * visitor already has the global chrome mounted in app/layout.tsx: FloatingNav
 * at the bottom, BrandHeader top-centre, DesktopNotificationsHeader top-right.
 * Rendered unconditionally it is a second navigation sitting on top of those.
 *
 * The old PublicNavbar handled this internally (it returned null when logged
 * in off the landing page), so pages could drop it in and not think about it.
 * Moving a page to the glass nav means it has to think about it. This is that
 * thinking, in one place, so /listings and /hiring cannot drift apart on it —
 * the same reason isGlassNavPath exists in i18n/routing.ts.
 */
export function PublicPageNav({
  audience = "barber",
  defaultMode = "light",
}: {
  audience?: "barber" | "client"
  defaultMode?: "light" | "dark"
}) {
  const { isLoading } = useUserRole()
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)

  useEffect(() => {
    if (typeof window === "undefined") return
    createClient()
      .auth.getUser()
      .then(({ data: { user } }: UserResponse) => setIsAuthenticated(!!user))
  }, [])

  // Undecided renders nothing rather than guessing. The nav is fixed-position
  // and reserves no layout space, so arriving a beat late moves nothing on the
  // page — whereas flashing a Join pill at someone who is signed in, or
  // blocking the server HTML on an auth check, both cost more.
  if (isLoading || isAuthenticated !== false) return null

  return <PublicNavGlass defaultMode={defaultMode} showModeToggle={false} audience={audience} />
}
